import axios from "axios";
import { Observable } from "rxjs";
import { ObservableHandler } from "../../../handlers";
import { parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import { pricingProductFromService, pricingProductListItemFromService, toServiceUpdateProductDefaultCpmRequest, toServiceUpsertStationGroupCpmRequestFromProduct } from "../../../models/verticalBilling";
import { ProductPricingFetchFailed, ProductPricingSaveFailed } from "./errors";
import { NotImplementedError } from "../../../errors";
import { toProductPricingQueryParams, toProductDetailQueryParams } from "../filters";
class PricingProducts extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "product-pricing", { atomize: true });
        this.url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/products`;
    }
    findItems(filters, sort) {
        return new Observable((subscriber) => {
            const idFilter = parseIdFilter(filters);
            const promise = idFilter.size === 1
                ? this.getProduct(idFilter.values().next().value).then((res) => [res.product])
                : this.getProductList(filters, sort);
            promise
                .then((result) => {
                subscriber.next(result);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    /**
     * @param filters - Filters for search and pagination.
     * @param sort - Optional sort configuration.
     * @return A promise resolving to array of products or Page of products.
     */
    async getProductList(filters = {}, sort) {
        const queryParams = toProductPricingQueryParams(filters, sort);
        const url = `${this.url}${queryParams}`;
        const pageSize = filters?.paging?.size || 50;
        return axios
            .get(url, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => {
            const products = res.data?.map((item) => pricingProductListItemFromService(item)) || [];
            if (isServicePage(res)) {
                return {
                    page: {
                        count: res.paging_info.count,
                        token: res.paging_info.token,
                        size: pageSize
                    },
                    data: products
                };
            }
            return products;
        })
            .catch((error) => {
            throw new ProductPricingFetchFailed(error);
        });
    }
    /**
     * @param productId - The id of a product to get.
     * @param filters - Optional filters for search and pagination of nested station groups.
     * @param sort - Optional sort configuration for nested station groups.
     * @return A promise resolving to product with billing info and pagination info.
     */
    async getProduct(productId, filters, sort) {
        const queryParams = toProductDetailQueryParams(filters, sort);
        const url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/product/${productId}${queryParams}`;
        return axios
            .get(url, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => ({
            product: pricingProductFromService(res.data),
            totalCount: res.paging_info?.count ?? 0,
            pageToken: res.paging_info?.token
        }))
            .catch((error) => {
            throw new ProductPricingFetchFailed(error);
        });
    }
    saveItem(data, options) {
        const updateTypes = this.resolveUpdateTypes(data, options?.updateType);
        return new Observable((observer) => {
            this.executeUpdates(data, updateTypes)
                .then((product) => {
                this.cache.reconcile("save", product);
                observer.next(product);
                observer.complete();
            })
                .catch((error) => {
                observer.error(new ProductPricingSaveFailed(error));
            });
        });
    }
    /**
     * Resolves which update types to execute.
     * If explicit updateType provided, uses that. Otherwise auto-detects from data.
     */
    resolveUpdateTypes(data, updateType) {
        if (updateType) {
            return Array.isArray(updateType) ? updateType : [updateType];
        }
        // pushes the updateTypes based on what fields are present in the data
        const types = [];
        if (data.defaultRetailCpm !== undefined) {
            types.push("defaultCpm");
        }
        if (data.billingInfo?.stationGroups?.some((sg) => sg.stationGroupId && sg.cpm != null)) {
            types.push("stationGroupCpms");
        }
        return types.length > 0 ? types : ["defaultCpm"];
    }
    /**
     * Executes all update types in parallel and returns refreshed product.
     */
    async executeUpdates(data, updateTypes) {
        if (!data.id) {
            return Promise.reject(new ProductPricingSaveFailed(null, "Product ID is required to update"));
        }
        await Promise.all(updateTypes.map((type) => type === "defaultCpm" ? this.putDefaultCpm(data) : this.postStationGroupCpms(data)));
        const detailUrl = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/product/${data.id}`;
        const { data: res } = await axios.get(detailUrl, { headers: { "Content-Type": "application/json" } });
        return pricingProductFromService(res.data);
    }
    /**
     * Sends PUT request to update default CPM (no refetch).
     */
    async putDefaultCpm(product) {
        const url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/product/${product.id}/default-cpm`;
        const request = toServiceUpdateProductDefaultCpmRequest(product);
        await axios.put(url, request, { headers: { "Content-Type": "application/json" } });
    }
    /**
     * Sends POST request to update station group CPMs (no refetch).
     */
    async postStationGroupCpms(product) {
        const url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/station-group-cpms`;
        const request = toServiceUpsertStationGroupCpmRequestFromProduct(product);
        await axios.post(url, request, { headers: { "Content-Type": "application/json" } });
    }
    /**
     * Converts from a service product to a client product.
     * @param product: the product to convert.
     * @return: the converted product.
     */
    toProduct(product) {
        return pricingProductFromService(product);
    }
    async deleteItem() {
        throw new NotImplementedError("delete");
    }
}
export default PricingProducts;
//# sourceMappingURL=index.js.map